import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Search, ChevronRight, ChevronDown, ChevronLeft, Check, Minus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { buildFarmGrouping } from '../../utils/areaOverlayUtils';
import './HierarchicalSelector.css';

const sortByName = (a, b) => {
  const nameA = (a?.areaName || a?.name || a?.id || '').toString();
  const nameB = (b?.areaName || b?.name || b?.id || '').toString();
  return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
};

export function HierarchicalSelector({
  farm,
  sections,
  stations,
  selectedSections,
  selectAllSections,
  onSectionsChange,
  onSelectAllChange,
  footerActions,
  selectionDisabled = false,
  drillState,
  onDrillChange,
}) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [focusPath, setFocusPath] = useState([]);

  const pathsEqual = (a = [], b = []) => {
    if (a.length !== b.length) return false;
    return a.every((item, idx) => item?.id === b[idx]?.id && item?.level === b[idx]?.level);
  };

  const applyFocusPath = (nextPath) => {
    setFocusPath(nextPath);
    if (typeof onDrillChange !== 'function') return;
    if (!Array.isArray(nextPath) || nextPath.length === 0) {
      onDrillChange(null);
      return;
    }
    const last = nextPath[nextPath.length - 1];
    if (last?.level && last?.id) {
      onDrillChange({ level: last.level, id: last.id });
      return;
    }
    onDrillChange(null);
  };

  const grouping = useMemo(() => {
    return buildFarmGrouping({
      farm,
      sections,
      stations,
    });
  }, [farm, sections, stations]);

  const allSectionIds = useMemo(() => {
    return grouping.sections.map(section => section.id).filter(Boolean);
  }, [grouping.sections]);

  const rootNodes = useMemo(() => {
    const buildStationNodes = (section) => {
      const stationList = Array.isArray(section.stations) ? [...section.stations] : [];
      stationList.sort((a, b) => (a?.stationCode || '').localeCompare(b?.stationCode || ''));
      return stationList.map((station, index) => {
        const stationKey = station.id || station.stationCode || `${section.id || 'section'}-${index}`;
        return {
          id: `station-${stationKey}`,
          name: station.stationCode || station.name || station.id || t('analytics.sections.station', { defaultValue: 'Station' }),
          level: 'station',
          station,
          sectionId: section.id,
          children: [],
        };
      });
    };

    const buildSectionNode = (section) => {
      const sectionId = section.id || section.areaName || section.name;
      const stationNodes = buildStationNodes(section);
      return {
        id: sectionId,
        name: section.areaName || section.name || sectionId,
        level: 'section',
        sectionIds: sectionId ? [sectionId] : [],
        sectionsCount: 1,
        stationsCount: stationNodes.length,
        children: stationNodes,
      };
    };

    const buildZoneNode = (zone) => {
      const sectionNodes = (zone.sections || []).slice().sort(sortByName).map(buildSectionNode);
      const sectionIds = Array.isArray(zone.sectionIds)
        ? zone.sectionIds.filter(Boolean)
        : sectionNodes.flatMap((node) => node.sectionIds || []);
      const stationsCount = typeof zone.totalCount === 'number'
        ? zone.totalCount
        : (zone.stations ? zone.stations.length : sectionNodes.reduce((sum, node) => sum + node.stationsCount, 0));
      return {
        id: zone.id,
        name: zone.areaName || zone.name || zone.id,
        level: 'zone',
        sectionIds,
        sectionsCount: sectionIds.length,
        stationsCount,
        children: sectionNodes,
      };
    };

    const buildQuadrantNode = (quadrant) => {
      const zoneNodes = (quadrant.zones || []).slice().sort(sortByName).map(buildZoneNode);
      const sectionIds = Array.isArray(quadrant.sectionIds)
        ? quadrant.sectionIds.filter(Boolean)
        : zoneNodes.flatMap((node) => node.sectionIds || []);
      const stationsCount = typeof quadrant.totalCount === 'number'
        ? quadrant.totalCount
        : (quadrant.stations ? quadrant.stations.length : zoneNodes.reduce((sum, node) => sum + node.stationsCount, 0));
      return {
        id: quadrant.id,
        name: quadrant.areaName || quadrant.name || quadrant.id,
        level: 'quadrant',
        sectionIds,
        sectionsCount: sectionIds.length,
        stationsCount,
        children: zoneNodes,
      };
    };

    if (grouping.groupingLevel === 'quadrant') {
      return grouping.quadrants.slice().sort(sortByName).map(buildQuadrantNode);
    }
    if (grouping.groupingLevel === 'zone') {
      return grouping.zones.slice().sort(sortByName).map(buildZoneNode);
    }
    return grouping.sections.slice().sort(sortByName).map(buildSectionNode);
  }, [grouping, t]);

  const groupingKey = useMemo(() => {
    return `${farm?.id || 'farm'}:${grouping.groupingLevel}:${grouping.sections.length}:${grouping.zones.length}:${grouping.quadrants.length}`;
  }, [farm?.id, grouping.groupingLevel, grouping.sections.length, grouping.zones.length, grouping.quadrants.length]);

  useEffect(() => {
    const initialExpanded = new Set(rootNodes.map(node => node.id));
    setExpandedIds(initialExpanded);
    if (!drillState?.level) {
      setFocusPath([]);
    }
  }, [drillState?.level, groupingKey, rootNodes]);

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const isSearchActive = normalizedQuery.length > 0;

  useEffect(() => {
    if (isSearchActive) {
      setFocusPath([]);
    }
  }, [isSearchActive]);

  useEffect(() => {
    if (!drillState?.level || !drillState?.id) {
      if (focusPath.length > 0) {
        setFocusPath([]);
      }
      return;
    }

    const nextPath = [];
    if (drillState.level === 'quadrant') {
      const quadrant = grouping.quadrants.find(item => item.id === drillState.id);
      if (quadrant) {
        nextPath.push({
          id: quadrant.id,
          name: quadrant.areaName || quadrant.name || quadrant.id,
          level: 'quadrant',
        });
      }
    } else if (drillState.level === 'zone') {
      if (grouping.groupingLevel === 'quadrant') {
        const quadrantId = grouping.zoneIdToQuadrantId?.get(drillState.id);
        const quadrant = grouping.quadrants.find(item => item.id === quadrantId);
        if (quadrant) {
          nextPath.push({
            id: quadrant.id,
            name: quadrant.areaName || quadrant.name || quadrant.id,
            level: 'quadrant',
          });
        }
      }
      const zone = grouping.zones.find(item => item.id === drillState.id);
      if (zone) {
        nextPath.push({
          id: zone.id,
          name: zone.areaName || zone.name || zone.id,
          level: 'zone',
        });
      }
    } else if (drillState.level === 'section') {
      const zoneId = grouping.sectionIdToZoneId?.get(drillState.id) || null;
      if (zoneId && grouping.groupingLevel === 'quadrant') {
        const quadrantId = grouping.zoneIdToQuadrantId?.get(zoneId);
        const quadrant = grouping.quadrants.find(item => item.id === quadrantId);
        if (quadrant) {
          nextPath.push({
            id: quadrant.id,
            name: quadrant.areaName || quadrant.name || quadrant.id,
            level: 'quadrant',
          });
        }
      }
      if (zoneId && grouping.groupingLevel !== 'section') {
        const zone = grouping.zones.find(item => item.id === zoneId);
        if (zone) {
          nextPath.push({
            id: zone.id,
            name: zone.areaName || zone.name || zone.id,
            level: 'zone',
          });
        }
      }
      const section = grouping.sections.find(item => item.id === drillState.id);
      if (section) {
        nextPath.push({
          id: section.id,
          name: section.areaName || section.name || section.id,
          level: 'section',
        });
      }
    }

    if (nextPath.length === 0) {
      if (focusPath.length > 0) setFocusPath([]);
      return;
    }
    if (!pathsEqual(focusPath, nextPath)) {
      setFocusPath(nextPath);
    }
  }, [drillState, focusPath, grouping]);

  const selectedSet = useMemo(() => {
    if (selectAllSections) {
      return new Set(allSectionIds);
    }
    return new Set(Array.isArray(selectedSections) ? selectedSections : []);
  }, [selectAllSections, selectedSections, allSectionIds]);

  const matchesStation = (station) => {
    if (!station) return false;
    const fields = [
      station.stationCode,
      station.status,
      station.sensorType,
      station.stationType,
      station.locationDescription,
    ].filter(Boolean).join(' ').toLowerCase();
    return fields.includes(normalizedQuery);
  };

  const matchesNode = (node) => {
    if (!node) return false;
    const name = (node.name || '').toLowerCase();
    return name.includes(normalizedQuery);
  };

  const filterNodes = (nodes) => {
    if (!isSearchActive) return nodes;
    return nodes.map((node) => {
      if (node.level === 'station') {
        return matchesStation(node.station) ? node : null;
      }
      const filteredChildren = filterNodes(node.children || []).filter(Boolean);
      if (matchesNode(node) || filteredChildren.length > 0) {
        return { ...node, children: filteredChildren };
      }
      return null;
    }).filter(Boolean);
  };

  const filteredNodes = useMemo(() => filterNodes(rootNodes), [rootNodes, isSearchActive, normalizedQuery]);

  const resolveFocus = useMemo(() => {
    if (isSearchActive || focusPath.length === 0) {
      return { focusNode: null, nodes: filteredNodes };
    }
    let currentNodes = filteredNodes;
    let currentNode = null;
    for (const step of focusPath) {
      currentNode = currentNodes.find(node => node.id === step.id);
      if (!currentNode) {
        return { focusNode: null, nodes: filteredNodes };
      }
      currentNodes = currentNode.children || [];
    }
    return { focusNode: currentNode, nodes: currentNodes };
  }, [filteredNodes, focusPath, isSearchActive]);

  const applySelection = (nextIds) => {
    const uniqueIds = Array.from(new Set((nextIds || []).filter(Boolean)));
    const uniqueSet = new Set(uniqueIds);
    const isAllSelected = allSectionIds.length > 0 && allSectionIds.every(id => uniqueSet.has(id));
    if (isAllSelected) {
      onSelectAllChange(true);
      onSectionsChange(allSectionIds);
    } else {
      onSelectAllChange(false);
      onSectionsChange(uniqueIds);
    }
  };

  const handleGroupToggle = (sectionIds = []) => {
    if (selectionDisabled) return;
    const ids = sectionIds.filter(Boolean);
    if (ids.length === 0) return;
    if (selectAllSections) {
      const remaining = allSectionIds.filter(id => !ids.includes(id));
      applySelection(remaining);
      return;
    }
    const selectedCount = ids.filter(id => selectedSet.has(id)).length;
    const isFullySelected = selectedCount === ids.length;
    const nextSelection = isFullySelected
      ? (Array.isArray(selectedSections) ? selectedSections.filter(id => !ids.includes(id)) : [])
      : Array.from(new Set([...(selectedSections || []), ...ids]));
    applySelection(nextSelection);
  };

  const handleSectionToggle = (sectionId) => {
    if (selectionDisabled) return;
    if (!sectionId) return;
    if (selectAllSections) {
      const remaining = allSectionIds.filter(id => id !== sectionId);
      applySelection(remaining);
      return;
    }
    const isSelected = selectedSet.has(sectionId);
    const nextSelection = isSelected
      ? (Array.isArray(selectedSections) ? selectedSections.filter(id => id !== sectionId) : [])
      : [...(selectedSections || []), sectionId];
    applySelection(nextSelection);
  };

  const toggleExpanded = (nodeId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const getSelectionState = (sectionIds = []) => {
    const ids = sectionIds.filter(Boolean);
    if (ids.length === 0) return 'none';
    if (selectAllSections) return 'all';
    const selectedCount = ids.filter(id => selectedSet.has(id)).length;
    if (selectedCount === 0) return 'none';
    if (selectedCount === ids.length) return 'all';
    return 'partial';
  };

  const getGroupMeta = (node) => {
    if (node.level === 'section') {
      return t('analytics.sections.stationCount', {
        count: node.stationsCount,
        defaultValue: `${node.stationsCount} stations`,
      });
    }
    const sectionsLabel = t('analytics.sections.countSections', {
      count: node.sectionsCount,
      defaultValue: `${node.sectionsCount} sections`,
    });
    const stationsLabel = t('analytics.sections.countStations', {
      count: node.stationsCount,
      defaultValue: `${node.stationsCount} stations`,
    });
    return `${sectionsLabel} · ${stationsLabel}`;
  };

  const getStationStatus = (station) => {
    if (!station) return 'unknown';
    if (station.status) return station.status;
    if (station.isActive === false) return 'inactive';
    return 'active';
  };

  const renderNode = (node, depth = 0) => {
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const isExpanded = isSearchActive ? true : expandedIds.has(node.id);
    const isStation = node.level === 'station';
    const selectionState = isStation
      ? (node.sectionId && (selectAllSections || selectedSet.has(node.sectionId)) ? 'all' : 'none')
      : getSelectionState(node.sectionIds || []);
    const isSelected = selectionState === 'all';
    const isPartial = selectionState === 'partial';
    const status = isStation ? getStationStatus(node.station) : null;
    const canDrill = !isSearchActive && hasChildren;

    return (
      <div key={node.id} className="hierarchical-selector__node">
        <div
          className={[
            'hierarchical-selector__item-row',
            isSelected ? 'hierarchical-selector__item-row--selected' : '',
            isPartial ? 'hierarchical-selector__item-row--partial' : '',
            selectionDisabled ? 'hierarchical-selector__item-row--disabled' : '',
          ].filter(Boolean).join(' ')}
          style={{ paddingLeft: depth * 16 }}
        >
          {hasChildren ? (
            <button
              type="button"
              className="hierarchical-selector__expander"
              onClick={() => toggleExpanded(node.id)}
              aria-label={isExpanded ? t('common.collapse', { defaultValue: 'Collapse' }) : t('common.expand', { defaultValue: 'Expand' })}
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          ) : (
            <span className="hierarchical-selector__expander-placeholder" />
          )}
          {isStation ? (
            <button
              type="button"
              className={`hierarchical-selector__checkbox ${isSelected ? 'hierarchical-selector__checkbox--checked' : ''}`}
              onClick={() => handleSectionToggle(node.sectionId)}
              aria-pressed={isSelected}
              aria-label={t('analytics.sections.toggleStation', { defaultValue: 'Toggle station selection' })}
              disabled={selectionDisabled || !node.sectionId}
            >
              {isSelected && <Check size={12} />}
            </button>
          ) : (
            <button
              type="button"
              className={`hierarchical-selector__checkbox ${
                isSelected ? 'hierarchical-selector__checkbox--checked' : ''
              } ${isPartial ? 'hierarchical-selector__checkbox--partial' : ''}`}
              onClick={() => handleGroupToggle(node.sectionIds || [])}
              aria-pressed={isSelected || isPartial}
              disabled={selectionDisabled}
            >
              {isSelected && <Check size={12} />}
              {isPartial && !isSelected && <Minus size={12} />}
            </button>
          )}
          <button
            type="button"
            className="hierarchical-selector__label"
            onClick={() => {
              if (hasChildren) {
                toggleExpanded(node.id);
              } else if (isStation && !selectionDisabled) {
                handleSectionToggle(node.sectionId);
              }
            }}
          >
            <div className="hierarchical-selector__label-main">
              {isStation && (
                <span className={`hierarchical-selector__status-dot hierarchical-selector__status-dot--${status}`} />
              )}
              <span className="hierarchical-selector__name">{node.name}</span>
            </div>
            <span className="hierarchical-selector__meta">
              {isStation
                ? t(`stations.status.${status}`, { defaultValue: status })
                : getGroupMeta(node)}
            </span>
          </button>
          {canDrill && (
            <button
              type="button"
              className="hierarchical-selector__drill"
              onClick={() => applyFocusPath([...focusPath, { id: node.id, name: node.name, level: node.level }])}
              aria-label={t('common.open', { defaultValue: 'Open' })}
            >
              <ChevronRight size={14} />
            </button>
          )}
        </div>
        {hasChildren && isExpanded && (
          <div className="hierarchical-selector__children">
            {node.children.map(child => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="hierarchical-selector">
      <div className="hierarchical-selector__search">
        <Search size={16} className="hierarchical-selector__search-icon" />
        <input
          type="text"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder={t('analytics.sections.searchPlaceholder', { defaultValue: 'Search groups or stations' })}
          className="hierarchical-selector__search-input"
        />
      </div>

      {resolveFocus.focusNode && (
        <div className="hierarchical-selector__breadcrumb">
          <button
            type="button"
            className="hierarchical-selector__breadcrumb-back"
              onClick={() => applyFocusPath(focusPath.slice(0, -1))}
          >
            <ChevronLeft size={14} />
            <span>{t('common.back', { defaultValue: 'Back' })}</span>
          </button>
          <div className="hierarchical-selector__breadcrumb-trail">
            {focusPath.map(item => item.name).join(' / ')}
          </div>
        </div>
      )}

      <div className="hierarchical-selector__list">
        {resolveFocus.nodes.length === 0 ? (
          <div className="hierarchical-selector__empty">
            {t('analytics.sections.noneFound', { defaultValue: 'No matching results' })}
          </div>
        ) : (
          resolveFocus.nodes.map(node => renderNode(node, 0))
        )}
      </div>
      {footerActions && (
        <div className="hierarchical-selector__footer">
          {footerActions}
        </div>
      )}
    </div>
  );
}

HierarchicalSelector.propTypes = {
  farm: PropTypes.object,
  sections: PropTypes.arrayOf(PropTypes.object),
  stations: PropTypes.arrayOf(PropTypes.object),
  selectedSections: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectAllSections: PropTypes.bool.isRequired,
  onSectionsChange: PropTypes.func.isRequired,
  onSelectAllChange: PropTypes.func.isRequired,
  footerActions: PropTypes.node,
  selectionDisabled: PropTypes.bool,
  drillState: PropTypes.shape({
    level: PropTypes.oneOf(['quadrant', 'zone', 'section']),
    id: PropTypes.string,
  }),
  onDrillChange: PropTypes.func,
};

